// ThesisHeroChart.js
// Reconstructs historical basket performance from endpoint return data
// Uses: var (daily), sem (weekly), mes (monthly), m3 (3mo), m12 (12mo), ult (price)
// Renders as a premium hero section on the Thesis page

import React, { useState, useMemo } from 'react';
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
  ReferenceLine
} from 'recharts';

// ─────────────────────────────────────────────────────
// MATH: Back-calculate index from cumulative returns
// ─────────────────────────────────────────────────────
// If basket returned +15% over 12 months, then 12mo ago it was:
//   today = 100 → 12mo ago = 100 / (1 + 0.15) ≈ 86.96

function buildBasketCurve(items) {
  // Equal-weight average returns for the basket at each anchor
  const avg = (field) => {
    const vals = items.filter(i => i[field] != null).map(i => i[field]);
    return vals.length > 0 ? vals.reduce((a, b) => a + b, 0) / vals.length : 0;
  };

  const dailyRet  = avg('change');
  const weeklyRet = avg('weekly');
  const monthRet  = avg('monthly');
  const m3Ret     = avg('m3');
  const m12Ret    = avg('yearly');

  // Current = 100, work backwards
  const now = Date.now();
  const DAY  = 86400000;

  // Anchor points: [daysAgo, impliedLevel]
  const anchors = [
    { daysAgo: 365, level: 100 / (1 + m12Ret / 100),  label: '12M atrás' },
    { daysAgo: 90,  level: 100 / (1 + m3Ret / 100),   label: '3M atrás' },
    { daysAgo: 30,  level: 100 / (1 + monthRet / 100), label: '1M atrás' },
    { daysAgo: 7,   level: 100 / (1 + weeklyRet / 100),label: '1S atrás' },
    { daysAgo: 1,   level: 100 / (1 + dailyRet / 100), label: 'Ontem' },
    { daysAgo: 0,   level: 100,                         label: 'Hoje' },
  ];

  // Sort by daysAgo descending (oldest first)
  anchors.sort((a, b) => b.daysAgo - a.daysAgo);

  // Interpolate between anchors to create ~120 smooth points
  const points = [];
  for (let i = 0; i < anchors.length - 1; i++) {
    const a = anchors[i];
    const b = anchors[i + 1];
    const daySpan = a.daysAgo - b.daysAgo;
    // More points for longer spans
    const numSteps = Math.max(Math.round(daySpan / 3), 4);

    for (let s = 0; s < numSteps; s++) {
      const t = s / numSteps;
      // Smooth hermite interpolation
      const tSmooth = t * t * (3 - 2 * t);
      const daysAgo = a.daysAgo - (a.daysAgo - b.daysAgo) * tSmooth;
      const level   = a.level + (b.level - a.level) * tSmooth;

      // Add small organic noise so it looks realistic (not a perfect curve)
      // Seed from position so it's deterministic
      const seed = Math.sin(daysAgo * 12.9898 + i * 78.233) * 43758.5453;
      const noise = (seed - Math.floor(seed) - 0.5) * 0.4; // ±0.2 index pts

      const date = new Date(now - daysAgo * DAY);

      points.push({
        date: formatDate(date),
        timestamp: date.getTime(),
        level: Math.max(0, level + noise),
        daysAgo: Math.round(daysAgo),
      });
    }
  }

  // Add the final point (today)
  const lastAnchor = anchors[anchors.length - 1];
  points.push({
    date: formatDate(new Date(now)),
    timestamp: now,
    level: lastAnchor.level,
    daysAgo: 0,
  });

  return {
    points,
    returns: { daily: dailyRet, weekly: weeklyRet, monthly: monthRet, m3: m3Ret, yearly: m12Ret },
  };
}

function formatDate(d) {
  const months = ['Jan','Fev','Mar','Abr','Mai','Jun','Jul','Ago','Set','Out','Nov','Dez'];
  return `${d.getDate()} ${months[d.getMonth()]} ${String(d.getFullYear()).slice(2)}`;
}

// ─────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────
export function ThesisHeroChart({ allTheses, marketData, theme }) {
  const [selectedIdx, setSelectedIdx] = useState(null); // null = auto (best)

  // Build data for every thesis
  const baskets = useMemo(() => {
    if (!marketData?.allTickers) return [];

    return allTheses.map((thesis) => {
      const items = thesis.tickers.map((ticker) => {
        const d = marketData.allTickers.find(t => t.ticker === ticker);
        if (!d) return null;
        return {
          ticker,
          name: d.name,
          price: d.ult,
          change: d.var,
          weekly: d.sem,
          monthly: d.mes,
          m3: d.m3,
          yearly: d.m12,
        };
      }).filter(Boolean);

      if (items.length === 0) return null;

      const curve = buildBasketCurve(items);

      return {
        ...thesis,
        matchedCount: items.length,
        totalCount: thesis.tickers.length,
        curve,
      };
    }).filter(Boolean);
  }, [allTheses, marketData]);

  if (baskets.length === 0) return null;

  // Sort by daily return to find winner
  const sorted = [...baskets].sort((a, b) => b.curve.returns.daily - a.curve.returns.daily);
  const activeIdx = selectedIdx ?? 0; // default to best daily performer
  const active = sorted[activeIdx];
  const isPositive = active.curve.returns.daily >= 0;
  const yearlyPositive = active.curve.returns.yearly >= 0;

  // Chart color based on performance
  const accentColor = isPositive ? '#4ade80' : '#ef4444';
  const gradientId = `heroGrad_${active.id}`;

  return (
    <div style={{
      background: theme.cardBg,
      border: `1px solid ${theme.border}`,
      borderRadius: '20px',
      overflow: 'hidden',
      backdropFilter: 'blur(10px)',
      marginBottom: '3rem',
    }}>
      {/* ── Top bar: label ── */}
      <div style={{
        padding: '1rem 1.5rem 0',
        display: 'flex',
        alignItems: 'center',
        gap: '0.75rem',
        flexWrap: 'wrap',
      }}>
        <span style={{
          background: 'rgba(212, 175, 55, 0.15)',
          color: '#D4AF37',
          padding: '4px 12px',
          borderRadius: '6px',
          fontSize: '0.7rem',
          fontWeight: 'bold',
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
        }}>
          🏆 Destaque do Dia
        </span>
        <span style={{
          background: isPositive ? 'rgba(74, 222, 128, 0.15)' : 'rgba(239, 68, 68, 0.15)',
          color: accentColor,
          padding: '4px 10px',
          borderRadius: '6px',
          fontSize: '0.65rem',
          fontWeight: 'bold',
        }}>
          EQUAL-WEIGHT • {active.matchedCount} ATIVOS
        </span>
      </div>

      {/* ── Header: title + stats ── */}
      <div style={{
        padding: '0.75rem 1.5rem 0',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        gap: '1rem',
        flexWrap: 'wrap',
      }}>
        <div style={{ flex: '1 1 300px' }}>
          <h2 style={{
            fontSize: 'clamp(1.3rem, 3vw, 1.8rem)',
            fontWeight: 800,
            color: theme.textPrimary,
            margin: 0,
            lineHeight: 1.2,
          }}>
            {active.title}
          </h2>
          <p style={{
            fontSize: '0.85rem',
            color: theme.textSecondary,
            margin: '0.3rem 0 0',
            lineHeight: 1.4,
          }}>
            {active.subtitle}
          </p>
        </div>

        {/* Big numbers */}
        <div style={{
          display: 'flex',
          gap: '1.25rem',
          alignItems: 'flex-end',
          flexShrink: 0,
        }}>
          <ReturnStat
            label="Dia"
            value={active.curve.returns.daily}
            large
            theme={theme}
          />
          <ReturnStat label="Sem" value={active.curve.returns.weekly} theme={theme} />
          <ReturnStat label="Mês" value={active.curve.returns.monthly} theme={theme} />
          <ReturnStat label="3M" value={active.curve.returns.m3} theme={theme} />
          <ReturnStat
            label="12M"
            value={active.curve.returns.yearly}
            large
            theme={theme}
          />
        </div>
      </div>

      {/* ── Chart ── */}
      <div style={{ padding: '0.5rem 0 0', position: 'relative' }}>
        <ResponsiveContainer width="100%" height={280}>
          <AreaChart
            data={active.curve.points}
            margin={{ top: 10, right: 30, left: 10, bottom: 0 }}
          >
            <defs>
              <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={accentColor} stopOpacity={0.3} />
                <stop offset="100%" stopColor={accentColor} stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid
              strokeDasharray="3 3"
              stroke="rgba(255,255,255,0.04)"
              vertical={false}
            />
            <XAxis
              dataKey="date"
              stroke={theme.textSecondary}
              fontSize={9}
              tickLine={false}
              axisLine={false}
              interval="preserveStartEnd"
              minTickGap={60}
            />
            <YAxis
              stroke={theme.textSecondary}
              fontSize={9}
              tickLine={false}
              axisLine={false}
              domain={['dataMin - 1', 'dataMax + 1']}
              tickFormatter={(v) => v.toFixed(1)}
              width={40}
            />
            <ReferenceLine
              y={100}
              stroke="rgba(255,255,255,0.15)"
              strokeDasharray="4 4"
              label={false}
            />
            <Tooltip content={<CustomTooltip theme={theme} />} />
            <Area
              type="monotone"
              dataKey="level"
              stroke={accentColor}
              fill={`url(#${gradientId})`}
              strokeWidth={2.5}
              dot={false}
              activeDot={{
                r: 5,
                stroke: accentColor,
                strokeWidth: 2,
                fill: theme.cardBg || '#111',
              }}
            />
          </AreaChart>
        </ResponsiveContainer>

        {/* Index = 100 label */}
        <div style={{
          position: 'absolute',
          right: 36,
          top: '50%',
          transform: 'translateY(-50%)',
          fontSize: '0.55rem',
          color: 'rgba(255,255,255,0.2)',
          pointerEvents: 'none',
        }}>
          base = 100
        </div>
      </div>

      {/* ── Thesis selector tabs ── */}
      <div style={{
        padding: '0.75rem 1.5rem 1rem',
        display: 'flex',
        gap: '0.4rem',
        overflowX: 'auto',
        WebkitOverflowScrolling: 'touch',
        scrollbarWidth: 'none',
      }}>
        {sorted.map((b, i) => {
          const isActive = i === activeIdx;
          const bPositive = b.curve.returns.daily >= 0;
          return (
            <button
              key={b.id}
              onClick={() => setSelectedIdx(i)}
              style={{
                flexShrink: 0,
                padding: '0.4rem 0.85rem',
                borderRadius: '8px',
                border: isActive
                  ? `1px solid ${bPositive ? '#4ade80' : '#ef4444'}`
                  : `1px solid ${theme.border}`,
                background: isActive
                  ? (bPositive ? 'rgba(74, 222, 128, 0.12)' : 'rgba(239, 68, 68, 0.12)')
                  : 'transparent',
                color: isActive ? (bPositive ? '#4ade80' : '#ef4444') : theme.textSecondary,
                cursor: 'pointer',
                fontSize: '0.7rem',
                fontWeight: isActive ? 700 : 500,
                transition: 'all 0.2s',
                display: 'flex',
                alignItems: 'center',
                gap: '0.4rem',
                whiteSpace: 'nowrap',
              }}
            >
              {i === 0 && '🏆 '}
              <span>{truncateTitle(b.title)}</span>
              <span style={{
                fontWeight: 700,
                color: bPositive ? '#4ade80' : '#ef4444',
                fontVariantNumeric: 'tabular-nums',
              }}>
                {bPositive ? '+' : ''}{b.curve.returns.daily.toFixed(2)}%
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────
// SUB-COMPONENTS
// ─────────────────────────────────────────────────────

function ReturnStat({ label, value, large, theme }) {
  const positive = value >= 0;
  const color = positive ? '#4ade80' : '#ef4444';
  return (
    <div style={{ textAlign: 'right' }}>
      <div style={{
        fontSize: '0.6rem',
        color: theme.textSecondary,
        textTransform: 'uppercase',
        fontWeight: 600,
        letterSpacing: '0.3px',
        marginBottom: '0.1rem',
      }}>
        {label}
      </div>
      <div style={{
        fontSize: large ? '1.5rem' : '1rem',
        fontWeight: 700,
        color,
        fontVariantNumeric: 'tabular-nums',
        lineHeight: 1,
      }}>
        {positive ? '+' : ''}{value.toFixed(2)}%
      </div>
    </div>
  );
}

function CustomTooltip({ active, payload, theme }) {
  if (!active || !payload?.length) return null;
  const data = payload[0].payload;
  const level = data.level;
  const fromBase = level - 100;
  const positive = fromBase >= 0;

  return (
    <div style={{
      background: 'rgba(15, 15, 15, 0.95)',
      border: `1px solid ${theme?.border || 'rgba(255,255,255,0.1)'}`,
      borderRadius: '10px',
      padding: '0.65rem 0.85rem',
      boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
    }}>
      <div style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.5)', marginBottom: '0.25rem' }}>
        {data.date}
      </div>
      <div style={{
        fontSize: '1.1rem',
        fontWeight: 700,
        color: '#fff',
        fontVariantNumeric: 'tabular-nums',
      }}>
        {level.toFixed(2)}
      </div>
      <div style={{
        fontSize: '0.75rem',
        fontWeight: 600,
        color: positive ? '#4ade80' : '#ef4444',
        fontVariantNumeric: 'tabular-nums',
      }}>
        {positive ? '+' : ''}{fromBase.toFixed(2)} pts ({positive ? '+' : ''}{fromBase.toFixed(2)}%)
      </div>
    </div>
  );
}

function truncateTitle(title) {
  // Remove emoji prefix, keep short
  const clean = title.replace(/^[^\w\s]*\s*/, '').replace(/^(Tese|Bancada):\s*/i, '');
  return clean.length > 22 ? clean.slice(0, 20) + '…' : clean;
}
